import { ColDef } from 'ag-grid-community'
import { Avatar, MenuItem, Select, styled } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { Button } from '../../../../components/buttons'
import { colorPalette } from '../../../../style/colorPalette'
import ReloadIcon from '../../../../components/icons/ReloadIcon'
import FilterButton from '../../../../containers/BulkSheetView/components/header/FilterButton'
import SortButton from '../../../../containers/BulkSheetView/components/header/SortButton'
import ColumnSettingButton from '../../../../containers/BulkSheetView/components/header/ColumnSettingButton'
import DisplayFormatButton from '../../../../containers/BulkSheetView/components/header/DisplayFormatButton'
import { SortedColumnState } from '../../../../model/bulkSheetColumnSortState'
import { intl } from '../../../../../i18n'
import { WbsItemTypeVO } from '../../../../../domain/value-object/WbsItemTypeVO'

const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
  margin: '10px 10px 5px 10px',
  overflowX: 'auto',
  overflowY: 'hidden',
  minHeight: '47px',
  gap: '10px',
})

const ReloadButton = styled(Button)({
  background: colorPalette.monotone[1],
  border: `1px solid ${colorPalette.monotone[3]}`,
  borderRadius: '50%',
  height: '28px',
  width: '28px',
  padding: '5px 8px',
  cursor: 'pointer',
  '&:hover': { background: colorPalette.monotone[1] },
})

const TypeIcon = styled(Avatar)({
  width: '20px',
  height: '20px',
})

const TypeMenuItemContent = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

// PBLのチーム選択(TeamSelect)と同じサイズ感に揃える
const WbsItemTypeSelect = styled(Select)({
  width: '175px',
  minWidth: '175px',
  height: '30px',
}) as unknown as typeof Select

type Props = {
  wbsItemType: string
  wbsItemTypes: WbsItemTypeVO[]
  onChangeWbsItemType: (code: string) => void
  onClickAddRow: () => void
  disableAddRow: boolean
  onReload: () => void
  // ヘッダーツールバー(フィルタ / ソート列 / 列設定 / 表示形式)
  filteredColumns: ColDef[]
  onDeleteFilteredColumn?: (column: ColDef) => void
  resetFilters: () => void
  sortColumnsState: SortedColumnState[]
  onDeleteSortedColumn?: (column: string | ColDef) => void
  onDeleteSortedAllColumns: () => void
  onChangeSortColumnState?: (
    column: string | ColDef,
    sort: 'asc' | 'desc' | null
  ) => void
  onClickColumnSettingButton: (event: React.MouseEvent) => void
  columnSettingOpen: boolean
  rowHeight: number
  onChangeRowHeight: (value: number) => void
}

const SubStatusHeader = ({
  wbsItemType,
  wbsItemTypes,
  onChangeWbsItemType,
  onClickAddRow,
  disableAddRow,
  onReload,
  filteredColumns,
  onDeleteFilteredColumn,
  resetFilters,
  sortColumnsState,
  onDeleteSortedColumn,
  onDeleteSortedAllColumns,
  onChangeSortColumnState,
  onClickColumnSettingButton,
  columnSettingOpen,
  rowHeight,
  onChangeRowHeight,
}: Props) => {
  return (
    <Header>
      <ReloadButton onClick={onReload}>
        <ReloadIcon />
      </ReloadButton>
      <WbsItemTypeSelect
        size="small"
        displayEmpty={true}
        value={wbsItemType}
        onChange={e => onChangeWbsItemType(e.target.value)}
        renderValue={selected => {
          if (!selected) {
            return (
              <em>
                {intl.formatMessage({ id: 'subStatus.selectWbsItemType' })}
              </em>
            )
          }
          const type = wbsItemTypes.find(v => v.code === selected)
          if (!type) return selected
          return (
            <TypeMenuItemContent>
              {type.iconUrl && (
                <TypeIcon variant="rounded" src={type.iconUrl} />
              )}
              <span>{type.name}</span>
            </TypeMenuItemContent>
          )
        }}
      >
        {wbsItemTypes.map(type => (
          <MenuItem key={type.code} value={type.code}>
            <TypeMenuItemContent>
              {type.iconUrl && (
                <TypeIcon variant="rounded" src={type.iconUrl} />
              )}
              <span>{type.name}</span>
            </TypeMenuItemContent>
          </MenuItem>
        ))}
      </WbsItemTypeSelect>
      <Button
        variant="filled"
        colorPattern="skyBlue"
        size="s"
        disabled={disableAddRow}
        onClick={onClickAddRow}
      >
        <AddRoundedIcon fontSize="small" />
        {intl.formatMessage({ id: 'subStatus.addRow' })}
      </Button>
      <FilterButton
        filteredColumns={filteredColumns}
        onDeleteFilteredColumn={onDeleteFilteredColumn}
        resetFilters={resetFilters}
      />
      <SortButton
        sortColumnsState={sortColumnsState}
        onDeleteSortedColumn={onDeleteSortedColumn}
        onDeleteSortedAllColumns={onDeleteSortedAllColumns}
        onChangeSortColumnState={onChangeSortColumnState}
      />
      <ColumnSettingButton
        onClick={onClickColumnSettingButton}
        open={columnSettingOpen}
      />
      <DisplayFormatButton
        height={rowHeight}
        onChangeHeight={onChangeRowHeight}
      />
    </Header>
  )
}

export default SubStatusHeader
